import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import ApiService from '../../services/ApiService';
import Loader from "../../components/Loader";
import Paginator from "../../components/Paginator";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { formatDate } from "../../utils/helper";
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import { BOOKING_STATUSES } from "../../assets/utils/statusColors";
import PageHeader from "../../components/ui/PageHeader";
import StatusBadge from "../../components/ui/StatusBadge";
import Avatar from "../../components/ui/Avatar";
import useBulkDelete from "../../hooks/useBulkDelete";
import BulkDeleteActions from "../../components/BulkDeleteActions";

const currency = process.env.REACT_APP_CURRENCY || 'AED';

const BookingsList = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [items, setItems] = useState([]);
    const [counts, setCounts] = useState({});
    const [statusList, setStatusList] = useState(BOOKING_STATUSES);
    const [isLoader, setIsLoader] = useState(false);
    const [pagination, setPagination] = useState({});

    const [apiData, setApiData] = useState({
        search: "",
        daterange: "",
        status: searchParams.get('status') || "",
        page: 1,
    });

    const fetchData = async () => {
        setIsLoader(true);
        try {
            const { data } = await ApiService.request({
                method: 'GET',
                url: '/bookings',
                data: apiData
            });
            if (data.status) {
                setItems(data.data);
                setCounts(data.counts || {});
                if (Array.isArray(data.statuses)) setStatusList(data.statuses);
                setPagination(data.pagination);
            }
        } catch (error) {
            // ApiService already shows the error toast
        } finally {
            setIsLoader(false);
        }
    };

    const {
        selectedIds,
        handleSelectAll,
        handleSelectItem,
        handleBulkDelete,
        clearSelection,
        isDeleting,
        hasSelection,
        selectionCount,
    } = useBulkDelete('bookings/bulk-delete', fetchData);

    useEffect(() => {
        fetchData();
    }, [apiData.page, apiData.search, apiData.daterange, apiData.status]);

    const setStatus = (status) => {
        setApiData(prev => ({ ...prev, status, page: 1 }));
        if (status) {
            setSearchParams({ status });
        } else {
            setSearchParams({});
        }
    };

    const clearFilter = () => {
        setApiData({ search: '', daterange: '', status: '', page: 1 });
        setSearchParams({});
    };

    const delData = async (id) => {
        const result = await Swal.fire({
            title: 'Are you sure?',
            text: 'you will not be able to revert it!',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            confirmButtonText: 'Yes, delete it!',
            cancelButtonColor: '#9E9B9B',
            cancelButtonText: 'No',
        });
        if (!result.isConfirmed) return;
        try {
            const { data } = await ApiService.request({ method: "DELETE", url: `bookings/${id}` });
            if (data.status) {
                fetchData();
                toast.success(data.message);
            } else {
                toast.error(data.message);
            }
        } catch (error) {
            // ApiService already shows the error toast
        }
    };

    // Booking lifecycle from the API, plus any unexpected status still present in the data
    const statuses = [...statusList, ...Object.keys(counts).filter(s => s !== 'all' && !statusList.includes(s))];
    const amount = (job) => {
        const value = job.pay_amount ?? job.sub_total ?? job.service_id?.price;
        return value != null ? `${currency} ${Number(value).toFixed(2)}` : '-';
    };

    return (
        <div className="hp-page">
            <PageHeader title="Bookings" subtitle="Every booking across service providers and facilities." />

            <div className="hp-card">
                <div className="hp-card-body pb-0">
                    <div className="hp-segment mb-4" role="tablist" aria-label="Filter by status">
                        <button type="button" role="tab" aria-selected={!apiData.status} className={!apiData.status ? 'active' : ''} onClick={() => setStatus('')}>
                            All<span className="hp-count">{counts.all || 0}</span>
                        </button>
                        {statuses.map(s => (
                            <button key={s} type="button" role="tab" aria-selected={apiData.status === s} className={apiData.status === s ? 'active' : ''} onClick={() => setStatus(s)}>
                                {s}<span className="hp-count">{counts[s] || 0}</span>
                            </button>
                        ))}
                    </div>
                    <div className="d-flex flex-wrap gap-3 align-items-center">
                        <div className="hp-header-search" style={{ flex: '1 1 280px', maxWidth: 380 }}>
                            <i className="bi bi-search"></i>
                            <input type="search" value={apiData.search} aria-label="Search bookings"
                                onChange={e => setApiData({ ...apiData, search: e.target.value, page: 1 })}
                                placeholder="Search code, customer, provider, location…" />
                        </div>
                        <div style={{ width: 240 }}>
                            <DatePicker
                                selectsRange={true}
                                startDate={apiData.daterange[0]}
                                endDate={apiData.daterange[1]}
                                onChange={e => setApiData({ ...apiData, daterange: e, page: 1 })}
                                isClearable={true}
                                className="form-control"
                                placeholderText="Booked between"
                            />
                        </div>
                        {(apiData.search || apiData.daterange || apiData.status) && (
                            <button type="button" onClick={clearFilter} className="btn btn-sm hp-btn-outline">Clear filters</button>
                        )}
                    </div>
                </div>

                {hasSelection && (
                    <div className="px-5 pt-4">
                        <BulkDeleteActions
                            selectionCount={selectionCount}
                            onDelete={handleBulkDelete}
                            isDeleting={isDeleting}
                            onClear={clearSelection}
                            itemName="booking(s)"
                        />
                    </div>
                )}

                <div className="table-responsive mt-4">
                    <table className="table hp-table table-hover align-middle mb-0">
                        <thead>
                            <tr>
                                <th style={{ width: 40 }}>
                                    <input
                                        type="checkbox"
                                        className="form-check-input"
                                        aria-label="Select all bookings"
                                        checked={items.length > 0 && selectedIds.length === items.length}
                                        onChange={() => handleSelectAll(items)}
                                        disabled={isDeleting}
                                    />
                                </th>
                                <th>Booking</th>
                                <th>Customer</th>
                                <th>Provider</th>
                                <th>Scheduled</th>
                                <th>Amount</th>
                                <th>Status</th>
                                <th className="text-end">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.length > 0 ? items.map((job) => (
                                <tr key={job._id}>
                                    <td>
                                        <input
                                            type="checkbox"
                                            className="form-check-input"
                                            aria-label={`Select booking ${job.job_code}`}
                                            checked={selectedIds.includes(job._id)}
                                            onChange={() => handleSelectItem(job._id)}
                                            disabled={isDeleting}
                                        />
                                    </td>
                                    <td>
                                        <Link to={`/bookings/${job._id}`} className="fw-bold text-dark text-hover-primary">#{job.job_code || '-'}</Link>
                                        <div className="text-muted fs-7 text-truncate" style={{ maxWidth: 220 }}>
                                            {job.service_id?.name || '—'}{job.service_id?.category_id?.title ? ` · ${job.service_id.category_id.title}` : ''}
                                        </div>
                                    </td>
                                    <td>
                                        {job.user_id ? (
                                            <Link to={`/users/${job.user_id._id}`} className="d-flex align-items-center gap-2 text-hover-primary">
                                                <Avatar src={job.user_id.picture} name={job.user_id.name} size={30} />
                                                <span className="text-dark text-nowrap">{job.user_id.name || '-'}</span>
                                            </Link>
                                        ) : '-'}
                                    </td>
                                    <td>
                                        {job.company_id ? (
                                            <>
                                                <div className="text-dark text-nowrap text-truncate" style={{ maxWidth: 180 }}>{job.company_id.name}</div>
                                                <div className="text-muted fs-8">{job.company_id.type === 'facility' ? 'Facility' : 'Service provider'}</div>
                                            </>
                                        ) : <span className="text-muted text-nowrap">Not assigned</span>}
                                    </td>
                                    <td className="text-nowrap">
                                        {job.date ? formatDate(job.date, 'DD MMM YYYY') : '-'}
                                        {job.time && <div className="text-muted fs-7">{job.time}</div>}
                                    </td>
                                    <td className="text-nowrap fw-semibold text-dark">{amount(job)}</td>
                                    <td><StatusBadge status={job.status} /></td>
                                    <td className="text-end text-nowrap">
                                        <Link to={`/bookings/${job._id}`} className="btn btn-sm hp-btn-outline me-1" title="View" aria-label={`View booking ${job.job_code}`}>
                                            <i className="bi bi-eye"></i>
                                        </Link>
                                        <button onClick={() => delData(job._id)} className="btn btn-sm hp-btn-outline text-danger" title="Delete" aria-label={`Delete booking ${job.job_code}`}>
                                            <i className="bi bi-trash"></i>
                                        </button>
                                    </td>
                                </tr>
                            )) : (
                                <tr>
                                    <td colSpan="8">
                                        {!isLoader && <div className="hp-empty"><i className="bi bi-calendar2-x"></i>No bookings found</div>}
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
                {isLoader && <div className="py-6"><Loader text="Loading" size="30" /></div>}
                <div className="hp-card-body d-flex justify-content-between align-items-center">
                    <span className="text-muted fs-7">{pagination?.total_records || 0} bookings</span>
                    {pagination.total_pages > 1 && (
                        <Paginator
                            page={pagination.current_page}
                            rows={pagination.per_page}
                            totalRecords={pagination.total_records}
                            onPageChange={page => setApiData(prev => ({ ...prev, page }))}
                        />
                    )}
                </div>
            </div>
        </div>
    );
};

export default BookingsList;
